import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Typography,
	CircularProgress,
	Alert,
	Chip,
	Paper,
	Button,
	Stack,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getMyDrinkRequests } from '../../../../services/Clint/NamedDrinkRequestClintApi';

const MyNamedDrinksRequests = () => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [requests, setRequests] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	useEffect(() => {
		fetchMyDrinkRequests();
	}, []);

	const fetchMyDrinkRequests = async () => {
		setLoading(true);
		setError('');
		try {
			const response = await getMyDrinkRequests();
			setRequests(response?.data || []);
		} catch (err) {
			console.error('خطأ في جلب طلبات تسمية مشروباتي:', err);
			setError(err.message || 'حدث خطأ أثناء جلب الطلبات');
		} finally {
			setLoading(false);
		}
	};

	const getStatusChip = (status) => {
		const statusMap = {
			pending: { label: 'قيد المراجعة', color: 'warning' },
			approved: { label: 'موافق عليه', color: 'success' },
			rejected: { label: 'مرفوض', color: 'error' },
		};
		const statusInfo = statusMap[status] || { label: status || '-', color: 'default' };
		return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
	};

	const formatDate = (date) => {
		if (!date) return '-';
		return new Date(date).toLocaleDateString('ar-EG', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
		});
	};

	const stats = useMemo(() => {
		const total = requests.length;
		const approved = requests.filter((r) => r?.status === 'approved').length;
		const pending = requests.filter((r) => r?.status === 'pending').length;
		return { total, approved, pending };
	}, [requests]);

	return (
		<Box dir="rtl" sx={{ mb: 4 }}>
			<Paper
				elevation={0}
				sx={{
					p: { xs: 2, md: 3 },
					mb: 2.5,
					borderRadius: 3,
					border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
					background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
				}}
			>
				<Stack
					direction={{ xs: 'column', md: 'row' }}
					spacing={2}
					alignItems={{ xs: 'stretch', md: 'center' }}
					justifyContent="space-between"
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
						<Box
							sx={{
								width: 50,
								height: 50,
								borderRadius: 2,
								display: 'grid',
								placeItems: 'center',
								bgcolor: alpha(theme.palette.primary.main, 0.12),
								color: 'primary.main',
							}}
						>
							<LocalCafeIcon sx={{ fontSize: 30 }} />
						</Box>
						<Box>
							<Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
								طلبات تسمية مشروباتي
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
								تابع حالة طلبات التسمية بسهولة وبشكل واضح.
							</Typography>
						</Box>
					</Box>

					<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
						<Chip color="primary" label={`الإجمالي: ${stats.total}`} sx={{ fontWeight: 700 }} />
						<Chip color="success" label={`موافق: ${stats.approved}`} sx={{ fontWeight: 700 }} />
						<Chip color="warning" label={`قيد المراجعة: ${stats.pending}`} sx={{ fontWeight: 700 }} />
						<Button
							variant="contained"
							startIcon={<RefreshIcon />}
							onClick={fetchMyDrinkRequests}
							disabled={loading}
							size="small"
							sx={{ borderRadius: 2, fontWeight: 700, minWidth: { xs: '100%', sm: 'auto' } }}
						>
							{loading ? 'جاري التحديث...' : 'تحديث'}
						</Button>
					</Stack>
				</Stack>
			</Paper>

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
					<CircularProgress />
				</Box>
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : requests.length === 0 ? (
				<Paper
					sx={{
						p: { xs: 3, md: 4 },
						textAlign: 'center',
						borderRadius: 3,
						bgcolor: alpha(theme.palette.grey[500], 0.08),
					}}
				>
					<LocalCafeIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
					<Typography variant="h6" color="text.secondary">
						لا توجد طلبات حتى الآن
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						عند إرسال طلب تسمية مشروب سيظهر هنا
					</Typography>
				</Paper>
			) : isMobile ? (
				<Stack spacing={1.25}>
					{requests.map((request) => (
						<Paper
							key={request.id}
							sx={{
								p: 1.5,
								borderRadius: 2.5,
								border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
								boxShadow: `0 8px 20px ${alpha(theme.palette.common.black, 0.05)}`,
							}}
						>
							<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
								<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
									#{request.id} {request.drink_name || '-'}
								</Typography>
								{getStatusChip(request.status)}
							</Stack>

							<Divider sx={{ my: 1 }} />

							<Stack spacing={0.75}>
								<Typography variant="body2" color="text.secondary">
									الاسم بالإنجليزي: {request.drink_name_en || '-'}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									ملاحظات: {request.notes || '-'}
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 700 }}>
									المشروب: {request.drink?.drink_name || request.drink?.name || '-'}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									السعر: {request.drink?.price || '0.00'} | الحرارة: {request.drink?.temperature || '-'}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									تاريخ الطلب: {formatDate(request.created_at)}
								</Typography>
							</Stack>
						</Paper>
					))}
				</Stack>
			) : (
				<TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden' }}>
					<Table>
						<TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.07) }}>
							<TableRow>
								<TableCell align="right">#</TableCell>
								<TableCell align="right">الاسم المقترح</TableCell>
								<TableCell align="right">الاسم بالإنجليزي</TableCell>
								<TableCell align="right">الحالة</TableCell>
								<TableCell align="right">ملاحظات الطلب</TableCell>
								<TableCell align="right">تفاصيل المشروب</TableCell>
								<TableCell align="right">تاريخ الطلب</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{requests.map((request) => (
								<TableRow
									key={request.id}
									sx={{
										'&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
										'& td': { borderBottomColor: alpha(theme.palette.divider, 0.6) },
									}}
								>
									<TableCell align="right">
										<Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
											#{request.id}
										</Typography>
									</TableCell>
									<TableCell align="right">{request.drink_name || '-'}</TableCell>
									<TableCell align="right">{request.drink_name_en || '-'}</TableCell>
									<TableCell align="right">{getStatusChip(request.status)}</TableCell>
									<TableCell align="right">{request.notes || '-'}</TableCell>
									<TableCell align="right">
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
											<Typography variant="body2" sx={{ fontWeight: 700 }}>
												{request.drink?.drink_name || request.drink?.name || '-'}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												السعر: {request.drink?.price || '0.00'}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												الحرارة: {request.drink?.temperature || '-'}
											</Typography>
										</Box>
									</TableCell>
									<TableCell align="right">{formatDate(request.created_at)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	);
};

export default MyNamedDrinksRequests;
